import { sortIvrMenuDtmfKeys } from "./ivrSchemeConstants";

type DraftData = Record<string, unknown>;

export type IvrSchedulerScheduleKind =
    | "daysweek"
    | "daysweek_advanced"
    | "once"
    | "once_advanced";

export function normalizeIvrSchedulerScheduleKind(
    raw: unknown
): IvrSchedulerScheduleKind {
    const s = String(raw ?? "")
        .trim()
        .toLowerCase();
    if (s === "once" || s === "daily") return "once";
    if (s === "once_advanced" || s === "once-advanced") return "once_advanced";
    if (s === "daysweek_advanced" || s === "daysweek-advanced") {
        return "daysweek_advanced";
    }
    return "daysweek";
}

export function ivrSchedulerKindDefersScheduleApiUntilBlockLink(
    kind: IvrSchedulerScheduleKind
): boolean {
    return (
        kind === "daysweek" ||
        kind === "once_advanced" ||
        kind === "daysweek_advanced"
    );
}

export function filterIvrSchedulerSlotRowsForScheduleApi<
    T extends { activeToId?: number },
>(kind: IvrSchedulerScheduleKind, rows: readonly T[]): T[] {
    if (!ivrSchedulerKindDefersScheduleApiUntilBlockLink(kind)) {
        return [...rows];
    }
    return rows.filter((r) => Number(r.activeToId) > 0);
}

type SchedulerDay = {
    dayOfWeek: number;
    enabled?: boolean;
    start?: string;
    end?: string;
};

type ScheduleItem = {
    elementId?: number | null;
    daysMask?: number | null;
    isActive?: boolean | null;
};

export type BuildIvrElementDraftDataOptions = {
    elementId?: number;
    schedules?: readonly ScheduleItem[];
};

const IVR_ELEMENT_TYPES = {
    SCHEDULER: "SCHEDULER",
    SCHEDULE: "SCHEDULE",
    MENU: "MENU",
} as const;

const DOW_TO_SCHEDULER_MASK: Record<number, number> = {
    1: 1,
    2: 2,
    3: 4,
    4: 8,
    5: 16,
    6: 32,
    0: 64,
};

function normalizeElementType(elementType: string): string {
    return elementType.toUpperCase();
}

function isSchedulerType(elementType: string): boolean {
    const type = normalizeElementType(elementType);
    return (
        type === IVR_ELEMENT_TYPES.SCHEDULER ||
        type === IVR_ELEMENT_TYPES.SCHEDULE
    );
}

function isMenuType(elementType: string): boolean {
    return normalizeElementType(elementType) === IVR_ELEMENT_TYPES.MENU;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getOptionalNumber(
    source: Record<string, unknown>,
    key: string
): number | null {
    const rawValue = source[key];

    if (rawValue == null || rawValue === "") {
        return null;
    }

    const parsedValue =
        typeof rawValue === "number"
            ? rawValue
            : Number(String(rawValue).trim());

    return Number.isFinite(parsedValue) ? parsedValue : null;
}

function getPositiveFlowId(source: Record<string, unknown>): number | null {
    const flowId = getOptionalNumber(source, "flowId");

    if (flowId == null || flowId <= 0) {
        return null;
    }

    return flowId;
}

function getSchedulerWeekdayMask(dayOfWeek: number): number {
    return DOW_TO_SCHEDULER_MASK[dayOfWeek] ?? 0;
}

function scheduleCoversWeekdayMask(
    schedules: readonly ScheduleItem[],
    elementId: number,
    dayMask: number
): boolean {
    if (!dayMask) {
        return false;
    }

    for (const schedule of schedules) {
        if (Number(schedule.elementId) !== elementId) {
            continue;
        }

        if (schedule.isActive === false) {
            continue;
        }

        const scheduleDaysMask = Number(schedule.daysMask) || 0;

        if ((scheduleDaysMask & dayMask) === dayMask) {
            return true;
        }
    }

    return false;
}

function hasFilledTimeRange(day: SchedulerDay): boolean {
    return Boolean(
        String(day.start ?? "").trim() && String(day.end ?? "").trim()
    );
}

function shouldIncludeSchedulerDay(
    day: SchedulerDay,
    options?: BuildIvrElementDraftDataOptions
): boolean {
    if (!day?.enabled) {
        return false;
    }

    if (!hasFilledTimeRange(day)) {
        return false;
    }

    const dayMask = getSchedulerWeekdayMask(Number(day.dayOfWeek));

    if (!dayMask) {
        return false;
    }

    const elementId = options?.elementId;
    const schedules = options?.schedules;

    if (elementId != null && schedules != null) {
        if (scheduleCoversWeekdayMask(schedules, elementId, dayMask)) {
            return false;
        }
    }

    return true;
}

function safeParseJsonObject(value: string): Record<string, unknown> | null {
    try {
        const parsed = JSON.parse(value) as unknown;
        return isObjectRecord(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function safeParseJsonUnknown(value: string): unknown | null {
    try {
        return JSON.parse(value) as unknown;
    } catch {
        return null;
    }
}

function safeStringify(value: unknown): string | undefined {
    try {
        return JSON.stringify(value);
    } catch {
        return undefined;
    }
}

export function parseIvrElementDraftData(raw: unknown): DraftData | null {
    if (raw == null || raw === "") {
        return null;
    }

    if (isObjectRecord(raw)) {
        return raw;
    }

    if (typeof raw !== "string") {
        return null;
    }

    let current: unknown = raw;

    for (let depth = 0; depth < 3; depth++) {
        if (typeof current === "string") {
            const parsedValue = safeParseJsonUnknown(current);

            if (parsedValue == null) {
                return null;
            }

            current = parsedValue;
        } else if (isObjectRecord(current)) {
            return current;
        } else {
            return null;
        }
    }

    return isObjectRecord(current) ? current : null;
}

export function mergeDraftDataWithPreviousForPayload(
    fresh: string | undefined,
    elementType: string,
    previousRaw: unknown
): string | undefined {
    if (fresh == null) {
        return undefined;
    }

    if (!isSchedulerType(elementType) && !isMenuType(elementType)) {
        return fresh;
    }

    const previousData = parseIvrElementDraftData(previousRaw);

    if (previousData == null) {
        return fresh;
    }

    const currentData = safeParseJsonObject(fresh);

    if (currentData == null) {
        return fresh;
    }

    if (currentData.playElementId == null && previousData.playElementId != null) {
        currentData.playElementId = previousData.playElementId;
    }

    if (currentData.flowId == null && previousData.flowId != null) {
        currentData.flowId = previousData.flowId;
    }

    if (isSchedulerType(elementType)) {
        if (
            currentData.schedulerElementId == null &&
            previousData.schedulerElementId != null
        ) {
            currentData.schedulerElementId = previousData.schedulerElementId;
        }
        const curKind = currentData.schedulerScheduleKind;
        const curKindStr =
            typeof curKind === "string" ? curKind.trim().toLowerCase() : "";
        const prevKind = previousData.schedulerScheduleKind;
        const curKindKnown =
            curKindStr === "once" ||
            curKindStr === "once_advanced" ||
            curKindStr === "once-advanced" ||
            curKindStr === "daysweek" ||
            curKindStr === "daysweek_advanced" ||
            curKindStr === "daysweek-advanced" ||
            curKindStr === "daily" ||
            curKindStr === "weekdays";
        if (
            !curKindKnown &&
            typeof prevKind === "string" &&
            prevKind.trim() !== ""
        ) {
            currentData.schedulerScheduleKind =
                normalizeIvrSchedulerScheduleKind(prevKind);
        }

        const curRows = currentData.schedulerSlotRows;
        const prevRows = previousData.schedulerSlotRows;
        const resolvedCurKind =
            typeof currentData.schedulerScheduleKind === "string"
                ? normalizeIvrSchedulerScheduleKind(
                      currentData.schedulerScheduleKind
                  )
                : null;
        const resolvedPrevKind =
            typeof previousData.schedulerScheduleKind === "string"
                ? normalizeIvrSchedulerScheduleKind(
                      previousData.schedulerScheduleKind
                  )
                : null;
        const schedulerKindUnchanged =
            resolvedCurKind != null &&
            resolvedPrevKind != null &&
            resolvedCurKind === resolvedPrevKind;
        if (
            !schedulerKindUnchanged &&
            resolvedCurKind === "daysweek"
        ) {
            delete currentData.schedulerSlotRows;
        }
        if (resolvedCurKind === "once_advanced") {
            delete currentData.schedulerShowOthers;
        }
        if (
            schedulerKindUnchanged &&
            (!Array.isArray(curRows) || curRows.length === 0) &&
            Array.isArray(prevRows) &&
            prevRows.length > 0
        ) {
            currentData.schedulerSlotRows = prevRows;
        }

        if (
            schedulerKindUnchanged &&
            resolvedCurKind !== "once_advanced" &&
            currentData.schedulerShowOthers === undefined &&
            previousData.schedulerShowOthers !== undefined
        ) {
            currentData.schedulerShowOthers = previousData.schedulerShowOthers;
        }
    }

    if (isMenuType(elementType)) {
        if (
            currentData.menuElementId == null &&
            previousData.menuElementId != null
        ) {
            currentData.menuElementId = previousData.menuElementId;
        }
        if (
            !("menuTimeoutRetries" in currentData) &&
            "menuTimeoutRetries" in previousData
        ) {
            currentData.menuTimeoutRetries = previousData.menuTimeoutRetries;
        }
        if (
            !("menuRetries" in currentData) &&
            "menuRetries" in previousData
        ) {
            currentData.menuRetries = previousData.menuRetries;
        }
    }

    return safeStringify(currentData) ?? fresh;
}

function weekdaySchedulerDraftShell(): SchedulerDay[] {
    return [1, 2, 3, 4, 5, 6, 0].map((dayOfWeek) => ({
        dayOfWeek,
        enabled: false,
        start: "",
        end: "",
    }));
}

export function isSchedulerDraftWeekdayEmptyShell(
    days: SchedulerDay[] | undefined | null
): boolean {
    if (!Array.isArray(days) || days.length !== 7) {
        return false;
    }
    return days.every(
        (d) =>
            !d?.enabled &&
            !String(d.start ?? "").trim() &&
            !String(d.end ?? "").trim()
    );
}

export type SchedulerDayLike = {
    dayOfWeek: number;
    enabled?: boolean;
    start?: string;
    end?: string;
    label?: string;
};

export function schedulerDaysweekHaveEnabledDays(
    days: SchedulerDayLike[] | undefined | null
): boolean {
    return (days ?? []).some(
        (d) =>
            !!d?.enabled &&
            !!String(d.start ?? "").trim() &&
            !!String(d.end ?? "").trim()
    );
}

export function stripSchedulerDaysEmptyDraftShell(
    days: SchedulerDayLike[] | undefined | null
): SchedulerDayLike[] {
    if (!days?.length) {
        return [];
    }
    if (isSchedulerDraftWeekdayEmptyShell(days as SchedulerDay[])) {
        return [];
    }
    return days;
}

export function resolveDaysweekSchedulerDaysSource(
    cfgDays: SchedulerDayLike[] | undefined | null,
    apiDraftDays: SchedulerDayLike[] | undefined | null,
    fallbackDays: SchedulerDayLike[] | undefined | null
): SchedulerDayLike[] {
    return mergeDaysweekSchedulerDaySources(
        fallbackDays,
        apiDraftDays,
        cfgDays
    );
}

const DAYS_WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

export function mergeDaysweekSchedulerDaySources(
    ...sources: (SchedulerDayLike[] | undefined | null)[]
): SchedulerDayLike[] {
    const merged = new Map<number, SchedulerDayLike>();
    for (const dow of DAYS_WEEK_ORDER) {
        merged.set(dow, {
            dayOfWeek: dow,
            enabled: false,
            start: "",
            end: "",
        });
    }
    for (const src of sources) {
        for (const d of stripSchedulerDaysEmptyDraftShell(src)) {
            const dow = Number(d.dayOfWeek);
            if (!Number.isFinite(dow) || !merged.has(dow)) {
                continue;
            }
            const start = String(d.start ?? "").trim();
            const end = String(d.end ?? "").trim();
            const prev = merged.get(dow)!;
            if (d.enabled && start && end) {
                merged.set(dow, {
                    dayOfWeek: dow,
                    label: d.label ?? prev.label,
                    enabled: true,
                    start,
                    end,
                });
            } else if (d.enabled === false) {
                merged.set(dow, {
                    dayOfWeek: dow,
                    label: d.label ?? prev.label,
                    enabled: false,
                    start: "",
                    end: "",
                });
            }
        }
    }
    return DAYS_WEEK_ORDER.map((dow) => merged.get(dow)!);
}

const SCHEDULER_ALL_DAYS_MASK = 127;

function trimTimeToHHMM(t: string): string {
    const p = String(t ?? "")
        .trim()
        .split(":");
    const h = String(parseInt(p[0] || "0", 10) || 0).padStart(2, "0");
    const m = String(parseInt(p[1] || "0", 10) || 0).padStart(2, "0");
    return `${h.slice(-2)}:${m.slice(0, 2)}`;
}

type DraftSchedulerSlotRow = {
    priority?: number;
    daysMask?: number;
    start?: string;
    end?: string;
    activeToId?: number;
    id?: number;
};

function isSingleWeekdayDaysMask(mask: number): boolean {
    const m = Number(mask) || 0;
    return m > 0 && (m & (m - 1)) === 0;
}

function schedulerSlotRowsForDraft(
    blockConfig: Record<string, unknown>,
    kind: IvrSchedulerScheduleKind
): DraftSchedulerSlotRow[] | undefined {
    if (kind !== "once_advanced" && kind !== "daysweek_advanced") {
        return undefined;
    }
    const rows =
        (blockConfig.schedulerSlotRows as DraftSchedulerSlotRow[] | undefined) ??
        [];
    const filtered =
        kind === "once_advanced"
            ? rows.filter(
                  (r) => (Number(r.daysMask) || 0) === SCHEDULER_ALL_DAYS_MASK
              )
            : rows.filter((r) =>
                  isSingleWeekdayDaysMask(Number(r.daysMask) || 0)
              );
    const serialized = filtered
        .filter(
            (r) =>
                String(r.start ?? "").trim() !== "" &&
                String(r.end ?? "").trim() !== ""
        )
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
        .map((r) => {
            const idRaw = r.id;
            const idNum =
                typeof idRaw === "number" ? idRaw : Number(idRaw ?? 0);
            const row: DraftSchedulerSlotRow = {
                priority: Number(r.priority) || 1,
                daysMask:
                    kind === "once_advanced"
                        ? SCHEDULER_ALL_DAYS_MASK
                        : Number(r.daysMask) || 0,
                start: trimTimeToHHMM(String(r.start ?? "")),
                end: trimTimeToHHMM(String(r.end ?? "")),
                activeToId: Number(r.activeToId) || 0,
            };
            if (Number.isFinite(idNum) && idNum > 0) {
                row.id = idNum;
            }
            return row;
        });
    return serialized.length > 0 ? serialized : undefined;
}

function buildSchedulerDraftData(
    blockConfig: Record<string, unknown>,
    options?: BuildIvrElementDraftDataOptions
): string | undefined {
    const rawDays = (blockConfig.schedulerDays as SchedulerDay[] | undefined) ?? [];

    const kind = normalizeIvrSchedulerScheduleKind(
        blockConfig.schedulerScheduleKind
    );
    let schedulerDays: SchedulerDay[];
    if (kind === "daysweek") {
        schedulerDays = rawDays.filter(
            (day) => !!day?.enabled && hasFilledTimeRange(day)
        );
    } else {
        schedulerDays = rawDays.filter((day) =>
            shouldIncludeSchedulerDay(day, options)
        );
        if (
            schedulerDays.length === 0 &&
            kind !== "once" &&
            kind !== "once_advanced" &&
            kind !== "daysweek_advanced"
        ) {
            schedulerDays = weekdaySchedulerDraftShell();
        }
    }

    const slotRows = schedulerSlotRowsForDraft(blockConfig, kind);
    const showOthers =
        kind === "once_advanced" ? false : blockConfig.schedulerShowOthers;

    return safeStringify({
        schemaVersion: 1,
        flowId: getPositiveFlowId(blockConfig),
        schedulerElementId: getOptionalNumber(blockConfig, "elementId"),
        playElementId: getOptionalNumber(blockConfig, "fromElementId"),
        schedulerScheduleKind: kind,
        schedulerDays,
        schedulerExtraWindow: blockConfig.schedulerExtraWindow ?? null,
        schedulerActiveToId: blockConfig.schedulerActiveToId ?? null,
        ...(slotRows != null ? { schedulerSlotRows: slotRows } : {}),
        ...(kind === "once_advanced" || typeof showOthers === "boolean"
            ? { schedulerShowOthers: kind === "once_advanced" ? false : showOthers }
            : {}),
    });
}

function menuRetryFieldFromBlock(
    blockConfig: Record<string, unknown>,
    key: "menuTimeoutRetries" | "menuRetries"
): number | null {
    const raw = blockConfig[key];
    if (raw == null || raw === "") return null;
    const n = typeof raw === "number" ? raw : Number(String(raw).trim());
    return Number.isFinite(n) ? n : null;
}

function buildMenuDraftData(
    blockConfig: Record<string, unknown>
): string | undefined {
    return safeStringify({
        schemaVersion: 1,
        flowId: getPositiveFlowId(blockConfig),
        menuElementId: getOptionalNumber(blockConfig, "elementId"),
        playElementId: getOptionalNumber(blockConfig, "fromElementId"),
        menuDtmfKeys: sortIvrMenuDtmfKeys(
            (blockConfig.menuDtmfKeys as string[] | undefined) ?? []
        ),
        menuDtmfRetries: blockConfig.menuDtmfRetries ?? {},
        menuTimeoutRetries: menuRetryFieldFromBlock(
            blockConfig,
            "menuTimeoutRetries"
        ),
        menuRetries: menuRetryFieldFromBlock(blockConfig, "menuRetries"),
    });
}

export function buildIvrElementDraftDataString(
    blockConfig: Record<string, unknown>,
    elementType: string,
    options?: BuildIvrElementDraftDataOptions
): string | undefined {
    if (isSchedulerType(elementType)) {
        return buildSchedulerDraftData(blockConfig, options);
    }

    if (isMenuType(elementType)) {
        return buildMenuDraftData(blockConfig);
    }

    return undefined;
}
